import { useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
    buildFreeInitialState, validateFreeForm,
} from "../../components/Main/auditorAndArtiste/subscriptions/CreateFreeForm";
import {
    buildPaidInitialState, validatePaidForm,
} from "../../components/Main/auditorAndArtiste/subscriptions/CreatePaidForm";
import { buildOwnerInitialState, validateOwner } from "./OwnerPicker";

/**
 * Builds the free-creation payload from form + owner state.
 * @param {Object} form - Free form state
 * @param {Object} owner - Owner state
 * @returns {Object}
 */
export function buildFreePayloadGlobal(form, owner) {
    return {
        owner_type: owner.owner_type,
        owner_id: Number(owner.owner_id),
        subscription_type: form.subscription_type,
        duration_months: form.duration_months,
        currency: form.currency,
        reason: form.reason.trim() || undefined,
    };
}

/**
 * Builds the paid-creation payload from form + owner state.
 * Amount is sent as null when left empty so the backend uses its default price.
 * @param {Object} form - Paid form state
 * @param {Object} owner - Owner state
 * @returns {Object}
 */
export function buildPaidPayloadGlobal(form, owner) {
    const trimmed = String(form.amount).trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    return {
        owner_type: owner.owner_type,
        owner_id: Number(owner.owner_id),
        subscription_type: form.subscription_type,
        currency: form.currency,
        amount: parsed,
        reason: form.reason.trim() || undefined,
    };
}

/**
 * POSTs the free creation payload, surfaces toasts and triggers refresh/close.
 * @param {Object} args - Submission arguments
 * @returns {Promise<void>}
 */
export async function submitFree({ payload, setLoading, onCreated, onClose }) {
    setLoading(true);
    try {
        const { data } = await axios.post("/admin/subscriptions/create-free", payload);
        toast.success("Abonnement gratuit créé");
        onCreated?.(data);
        onClose();
    } catch (error) {
        const msg = error?.response?.data || "Échec de la création";
        toast.error(typeof msg === "string" ? msg : "Échec de la création");
    } finally {
        setLoading(false);
    }
}

/**
 * POSTs the paid creation payload and stores the setup_link result for display.
 * @param {Object} args - Submission arguments
 * @returns {Promise<void>}
 */
export async function submitPaid({ payload, setLoading, setPaidResult, onCreated }) {
    setLoading(true);
    try {
        const { data } = await axios.post("/admin/subscriptions/create-paid", payload);
        toast.success("Abonnement créé — lien généré");
        setPaidResult(data);
        onCreated?.(data);
    } catch (error) {
        const msg = error?.response?.data || "Échec de la création";
        toast.error(typeof msg === "string" ? msg : "Échec de la création");
    } finally {
        setLoading(false);
    }
}

/**
 * Resets all dialog state when the dialog opens (mode, owner, forms, paid result).
 * @param {Object} args - Reset arguments
 */
export function useResetOnOpen({ open, setMode, setOwner, setFreeForm, setPaidForm, setPaidResult }) {
    useEffect(() => {
        if (!open) return;
        setMode("free");
        setOwner(buildOwnerInitialState());
        setFreeForm(buildFreeInitialState());
        setPaidForm(buildPaidInitialState());
        setPaidResult(null);
    }, [open, setMode, setOwner, setFreeForm, setPaidForm, setPaidResult]);
}

/**
 * Validates owner + active form, builds the payload and dispatches the matching submit.
 * @param {Object} args - Submit arguments
 * @returns {Promise<void>}
 */
export async function handleCreateSubmit({ mode, owner, freeForm, paidForm, setLoading, setPaidResult, onCreated, onClose }) {
    const ownerErr = validateOwner(owner);
    if (ownerErr) { toast.error(ownerErr); return; }
    if (mode === "free") {
        const err = validateFreeForm(freeForm);
        if (err) { toast.error(err); return; }
        await submitFree({ payload: buildFreePayloadGlobal(freeForm, owner), setLoading, onCreated, onClose });
        return;
    }
    const err = validatePaidForm(paidForm);
    if (err) { toast.error(err); return; }
    await submitPaid({ payload: buildPaidPayloadGlobal(paidForm, owner), setLoading, setPaidResult, onCreated });
}
